import { allArticles, knowledgeCategories as fallbackCategories } from "@/mocks/knowledgeData";

export const allKnowledgeCategoryLabel = "全部";

export type KnowledgeArticle = {
  id: number;
  slug: string;
  title: string;
  articleCategoryId?: number | null;
  category: string;
  summary: string;
  content: string;
  image: string;
  author: string;
  readTime: number;
  publishedAt: string | null;
  date: string;
  metaTitle?: string | null;
  metaDescription?: string | null;
  isFeatured: boolean;
  isPublished: boolean;
  sortOrder: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type KnowledgeArticlePayload = {
  Slug: string;
  Title: string;
  ArticleCategoryId: number | null;
  Category: string;
  Summary: string;
  Content: string;
  CoverImageUrl: string;
  Author: string;
  ReadTimeMinutes: number;
  PublishedAt: string | null;
  MetaTitle: string;
  MetaDescription: string;
  IsFeatured: boolean;
  IsPublished: boolean;
  SortOrder: number;
};

export type KnowledgeArticleCategory = {
  id: number;
  name: string;
  description?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type ApiArticle = {
  ArticleId: string | number;
  Slug: string;
  Title: string;
  ArticleCategoryId?: string | number | null;
  Category: string;
  Summary: string;
  Content: string;
  CoverImageUrl: string;
  Author: string;
  ReadTimeMinutes: string | number;
  PublishedAt?: string | null;
  MetaTitle?: string | null;
  MetaDescription?: string | null;
  IsFeatured?: boolean;
  IsPublished?: boolean;
  SortOrder?: string | number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

type ApiArticleCategory = {
  ArticleCategoryId: string | number;
  Name: string;
  Description?: string | null;
  IsActive?: boolean;
  SortOrder?: string | number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

type ApiResponse<T> = T & {
  ok: boolean;
  message?: string;
};

const adminCsrfKey = "fulling_admin_csrf";

function getAdminCsrfToken() {
  return sessionStorage.getItem(adminCsrfKey) || "";
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "資料處理失敗，請稍後再試。");
  }
  return data as T;
}

function adminHeaders(json = true) {
  return {
    Accept: "application/json",
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(getAdminCsrfToken() ? { "X-CSRF-Token": getAdminCsrfToken() } : {}),
  };
}

function formatDate(value?: string | null) {
  if (!value) return "";
  return value.slice(0, 10);
}

function normalizeArticle(article: ApiArticle): KnowledgeArticle {
  return {
    id: Number(article.ArticleId),
    slug: article.Slug || String(article.ArticleId),
    title: article.Title || "",
    articleCategoryId: article.ArticleCategoryId ? Number(article.ArticleCategoryId) : null,
    category: article.Category || "",
    summary: article.Summary || "",
    content: article.Content || "",
    image: article.CoverImageUrl || "",
    author: article.Author || "",
    readTime: Number(article.ReadTimeMinutes || 1),
    publishedAt: article.PublishedAt || null,
    date: formatDate(article.PublishedAt || article.CreatedAt),
    metaTitle: article.MetaTitle || null,
    metaDescription: article.MetaDescription || null,
    isFeatured: article.IsFeatured === true,
    isPublished: article.IsPublished !== false,
    sortOrder: Number(article.SortOrder || 100),
    createdAt: article.CreatedAt || null,
    updatedAt: article.UpdatedAt || null,
  };
}

function normalizeArticleCategory(category: ApiArticleCategory): KnowledgeArticleCategory {
  return {
    id: Number(category.ArticleCategoryId),
    name: category.Name || "",
    description: category.Description || "",
    isActive: category.IsActive !== false,
    sortOrder: Number(category.SortOrder || 100),
    createdAt: category.CreatedAt || null,
    updatedAt: category.UpdatedAt || null,
  };
}

function normalizeMockArticle(article: (typeof allArticles)[number]): KnowledgeArticle {
  return {
    id: article.id,
    slug: String(article.id),
    title: article.title,
    articleCategoryId: null,
    category: article.category,
    summary: article.summary,
    content: article.content,
    image: article.image,
    author: article.author,
    readTime: article.readTime,
    publishedAt: `${article.date} 00:00:00`,
    date: article.date,
    metaTitle: article.title,
    metaDescription: article.summary,
    isFeatured: article.id === 1,
    isPublished: true,
    sortOrder: article.id * 10,
  };
}

export async function fetchPublicKnowledgeArticles(params: { Category?: string; Keyword?: string; Page?: number; PerPage?: number } = {}) {
  try {
    const query = new URLSearchParams();
    if (params.Category && params.Category !== allKnowledgeCategoryLabel) query.set("Category", params.Category);
    if (params.Keyword) query.set("Keyword", params.Keyword);
    if (params.Page) query.set("Page", String(params.Page));
    if (params.PerPage) query.set("PerPage", String(params.PerPage));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    const data = await fetch(`/api/knowledge-articles${suffix}`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    }).then((response) => parseJsonResponse<{
      ok: boolean;
      Articles: ApiArticle[];
      FeaturedArticle?: ApiArticle | null;
      Categories?: string[];
    }>(response));

    return {
      articles: (data.Articles || []).map(normalizeArticle),
      featuredArticle: data.FeaturedArticle ? normalizeArticle(data.FeaturedArticle) : null,
      categories: data.Categories?.length ? [allKnowledgeCategoryLabel, ...data.Categories.filter((category) => category !== allKnowledgeCategoryLabel)] : fallbackCategories,
    };
  } catch {
    const articles = allArticles.map(normalizeMockArticle);
    return {
      articles,
      featuredArticle: articles.find((article) => article.isFeatured) || articles[0] || null,
      categories: fallbackCategories,
    };
  }
}

export async function fetchPublicKnowledgeArticle(articleKey: string | number) {
  try {
    const data = await fetch(`/api/knowledge-articles/${articleKey}`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    }).then((response) => parseJsonResponse<{
      ok: boolean;
      Article: ApiArticle;
      RelatedArticles?: ApiArticle[];
    }>(response));

    return {
      article: normalizeArticle(data.Article),
      relatedArticles: (data.RelatedArticles || []).map(normalizeArticle),
    };
  } catch {
    const articles = allArticles.map(normalizeMockArticle);
    const article = articles.find((item) => item.id === Number(articleKey) || item.slug === String(articleKey)) || null;
    if (!article) return { article: null, relatedArticles: [] };
    const relatedArticles = [
      ...articles.filter((item) => item.id !== article.id && item.category === article.category),
      ...articles.filter((item) => item.id !== article.id && item.category !== article.category),
    ].slice(0, 3);
    return { article, relatedArticles };
  }
}

export async function listAdminKnowledgeArticles(params: { Status?: string; Category?: string; Keyword?: string; Page?: number; PerPage?: number }) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.Category) query.set("Category", params.Category);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const data = await fetch(`/api/admin/knowledge-articles${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{
    ok: boolean;
    Articles: ApiArticle[];
    Counts?: { Published: number; Hidden: number };
    Categories?: string[];
    Pagination: { Page: number; PerPage: number; Total: number; TotalPages: number };
  }>(response));

  return {
    ...data,
    Articles: data.Articles.map(normalizeArticle),
    Categories: data.Categories || [],
  };
}

export async function getAdminKnowledgeArticle(articleId: string | number) {
  const data = await fetch(`/api/admin/knowledge-articles/${articleId}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{ ok: boolean; Article: ApiArticle }>(response));
  return normalizeArticle(data.Article);
}

export async function saveAdminKnowledgeArticle(articleId: string | number | null, payload: KnowledgeArticlePayload) {
  const path = articleId ? `/api/admin/knowledge-articles/${articleId}` : "/api/admin/knowledge-articles";
  const method = articleId ? "PUT" : "POST";
  const data = await fetch(path, {
    method,
    credentials: "include",
    headers: adminHeaders(true),
    body: JSON.stringify(payload),
  }).then((response) => parseJsonResponse<{ ok: boolean; Article: ApiArticle }>(response));
  return normalizeArticle(data.Article);
}

export type KnowledgeArticleCategoryPayload = {
  Name: string;
  Description: string;
  IsActive: boolean;
  SortOrder: number;
};

export async function listAdminKnowledgeArticleCategories() {
  const data = await fetch("/api/admin/knowledge-article-categories", {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{ ok: boolean; Categories: ApiArticleCategory[] }>(response));
  return data.Categories.map(normalizeArticleCategory);
}

export async function saveAdminKnowledgeArticleCategory(categoryId: number | null, payload: KnowledgeArticleCategoryPayload) {
  const path = categoryId ? `/api/admin/knowledge-article-categories/${categoryId}` : "/api/admin/knowledge-article-categories";
  const method = categoryId ? "PUT" : "POST";
  const data = await fetch(path, {
    method,
    credentials: "include",
    headers: adminHeaders(true),
    body: JSON.stringify(payload),
  }).then((response) => parseJsonResponse<{ ok: boolean; Category: ApiArticleCategory }>(response));
  return normalizeArticleCategory(data.Category);
}

export async function deleteAdminKnowledgeArticleCategory(categoryId: number) {
  return fetch(`/api/admin/knowledge-article-categories/${categoryId}`, {
    method: "DELETE",
    credentials: "include",
    headers: adminHeaders(false),
  }).then((response) => parseJsonResponse<{ ok: boolean }>(response));
}

export async function deleteAdminKnowledgeArticle(articleId: string | number) {
  return fetch(`/api/admin/knowledge-articles/${articleId}`, {
    method: "DELETE",
    credentials: "include",
    headers: adminHeaders(false),
  }).then((response) => parseJsonResponse<{ ok: boolean }>(response));
}

export async function uploadAdminKnowledgeArticleImage(file: File) {
  const formData = new FormData();
  formData.append("Image", file);
  const data = await fetch("/api/admin/knowledge-articles/upload-image", {
    method: "POST",
    credentials: "include",
    headers: adminHeaders(false),
    body: formData,
  }).then((response) => parseJsonResponse<{ ok: boolean; Url: string }>(response));
  return data.Url;
}

export async function deleteAdminKnowledgeArticleImage(url: string) {
  return fetch("/api/admin/knowledge-articles/upload-image", {
    method: "DELETE",
    credentials: "include",
    headers: adminHeaders(true),
    body: JSON.stringify({ Url: url }),
  }).then((response) => parseJsonResponse<{ ok: boolean }>(response));
}
